import {
  Component,
  DestroyRef,
  inject,
  input,
  signal,
  type OnInit,
} from '@angular/core';
import { HomebatteryWizardUiComponent } from '@feh/homebattery-ui';
import {
  HomeBatteryWizardRendering,
  type HomeBatteryWizardFormControls,
  type HomeBatteryWizardFormData,
} from '@feh/homebattery-models';
import { Store } from '@ngrx/store';
import {
  homeBatteryWizardConsumptionQuestionnaireOpenModalAction,
  homeBatteryWizardFormSubmitAction,
  parsedHomebatteryFunnelToolTrackingConfig,
  selectHomeBatteryConsumptionEstimateValueState,
} from '@feh/homebattery-store';
import type { FormGroup } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FehAddressCheckService } from '@innogy/feh/services';
import * as Sentry from '@sentry/angular';

import {
  createHomeBatteryForm,
  initializeHomeBatteryFunnelComponent,
} from './homebattery-wizard.container.helpers';

@Sentry.TraceClass({ name: 'FEIT-Homebattery-Wizard-Container' })
@Component({
  selector: 'wl-homebattery-wizard-container',
  templateUrl: './homebattery-wizard.container.component.html',
  standalone: true,
  imports: [HomebatteryWizardUiComponent],
  providers: [FehAddressCheckService],
})
export class HomebatteryWizardContainerComponent implements OnInit {
  public readonly rendering = input.required<HomeBatteryWizardRendering>();
  public readonly store$ = inject(Store);
  public homeBatteryForm!: FormGroup<HomeBatteryWizardFormControls>;

  private readonly destroyRef$ = inject(DestroyRef);
  protected readonly isLoading = signal(false);
  protected readonly addressCheckService = inject(FehAddressCheckService);
  protected trackToolConfig = parsedHomebatteryFunnelToolTrackingConfig;

  ngOnInit(): void {
    this.store$.dispatch(
      initializeHomeBatteryFunnelComponent(
        'wizard',
        this.rendering().fields.FunnelSettings
      )
    );

    this.homeBatteryForm = createHomeBatteryForm(this.rendering());

    this.store$
      .select(selectHomeBatteryConsumptionEstimateValueState)
      .pipe(takeUntilDestroyed(this.destroyRef$))
      .subscribe((val) => {
        if (val) {
          this.homeBatteryForm.controls.consumptionPerYear.setValue(val);
        }
      });
  }

  submit() {
    this.homeBatteryForm.markAllAsTouched();
    if (this.homeBatteryForm.valid) {
      const formData = this.homeBatteryForm.value as HomeBatteryWizardFormData;
      this.store$.dispatch(homeBatteryWizardFormSubmitAction(formData));
    }
  }

  openModal() {
    this.store$.dispatch(
      homeBatteryWizardConsumptionQuestionnaireOpenModalAction()
    );
  }
}
